/**
 * FILE ATTACHMENTS: the caps, the clocks, and the one number that is declared and
 * deliberately unused (`docs/decisions/file-attachments.md` D3, D4, D5, D8, D21).
 *
 * Here beside `csv.ts`'s own caps for the reason that file gives: a cap the browser
 * pre-checks and the server refuses on has to be ONE number, or the two halves drift and
 * a user meets a limit the screen said was not there.
 *
 * NOTHING HERE GROWS UNBOUNDED (D21). Attachments are the first artefact in this product
 * meant to be permanent, so they cannot have a retention clock the way an import's parsed
 * rows do - a signed contract is not garbage after 90 days. The bound is three caps
 * enforced at the door plus the two graces below.
 */

/**
 * D3. Just above `CSV_MAX_FILE_BYTES`' 20 MiB and inside the research's 5-50 MB
 * buffered-path cluster (Airtable 5, Attio 50, Twenty 10, HubSpot 20). The two artefacts
 * this product actually carries are a scanned contract PDF and a phone photo, and both fit
 * with room. A video does not, and a video is not CRM data.
 *
 * It is BOTH the declared cap the reserve route refuses on and the `bodyLimit` of the
 * octet-stream parser, which is the one that actually holds.
 */
export const FILE_MAX_BYTES = 25 * 1024 * 1024;

/**
 * D4: the prefix the magic-byte table reads. Twenty's number, generous for a table of a
 * dozen signatures, kept so a future signature needing a deeper offset does not need a
 * constant change.
 */
export const FILE_SNIFF_BYTES = 64 * 1024;

/**
 * D21. The research's default, and the practical ceiling of a chip list in a details
 * column (D1): an object that genuinely needs fifty documents per record wants a related
 * OBJECT with its own list, which the product already builds.
 */
export const FILES_FIELD_DEFAULT_MAX = 10;

/**
 * D21. Twenty's ceiling is 60 beside a 10 MB per-file cap - 600 MB per cell. Ours is
 * 25 x 25 MiB = 625 MB per cell, the same order of magnitude reached with a number a
 * person can picture.
 */
export const FILES_FIELD_MAX = 25;

/**
 * D8: set on UNLINK, and it is NOT a user undo - there is no trash screen and no restore
 * button, so calling it undo would advertise a mechanism with no consumer.
 *
 * Its two real consumers are an in-flight download that started before the removal
 * committed, and an optimistic UI update the server rejects. Both are measured in seconds;
 * an hour costs nothing and it must EXCEED any future signed-URL TTL, which is why
 * `FILE_SIGNED_URL_TTL_MINUTES` below is declared at all.
 */
export const ATTACHMENT_DETACHED_GRACE_MINUTES = 60;

/**
 * D8, D21: set at RESERVE. Matches Twenty's `PENDING_FILE_MAX_AGE_MS` for the same reason -
 * an upload wizard left open over lunch, or a tab that crashed after reserving. Notion's
 * equivalent is an hour, which is tighter than a human work rhythm.
 */
export const ATTACHMENT_UNCLAIMED_GRACE_HOURS = 24;

/**
 * DECLARED AND UNUSED BY DESIGN (D5).
 *
 * There is no signed URL in this product: the API is same-origin with the application
 * behind Caddy, so a subresource request already carries the session cookie and the
 * permission check the signed URL exists to replace is already on the request. If an
 * object-storage driver ever arrives, D5 fixes the shape (HMAC-SHA256 over
 * `"${fileId}.${expiresAtUnix}"`) and this is the TTL it would use - short, because trap 9
 * is about long-lived bearer tokens.
 *
 * It exists as a constant so obligation 5 can assert the inequality against the detached
 * grace above: a future TTL raised past the grace would let a URL outlive the bytes it
 * points at, and that has to fail a build rather than a support ticket.
 */
export const FILE_SIGNED_URL_TTL_MINUTES = 15;

/** The stored shape of one attachment, in the FIXED key order D7 makes load-bearing. */
export interface RecordFileItem {
  readonly fileId: string;
  readonly label: string;
  readonly byteCount: number;
  readonly contentType: string;
}

/** The WIRE shape a client sends: an id and the name it had on the way in, nothing else. */
export interface RecordFileRef {
  readonly fileId: string;
  readonly label: string;
}

/**
 * ONE ITEM, IN THE DECLARED KEY ORDER, whatever order it arrived in.
 *
 * THE ORDER IS LOAD-BEARING and the reason is INV-4's zero-change rule: `sameLogicalValue`
 * is `JSON.stringify` equality, so two arrays that differ only in key order read as a
 * CHANGE and a re-saved untouched form emits an `updated` event with a diff nobody made.
 *
 * AND THE ENRICHMENT ALONE CANNOT GUARANTEE IT, which is the thing the design document did
 * not account for: the cell is a `jsonb` column, and PostgreSQL NORMALISES jsonb keys (by
 * length, then bytewise) rather than preserving them. So a value written as
 * `{fileId, label, byteCount, contentType}` comes back as `{label, fileId, byteCount,
 * contentType}`, and every re-save would look like an edit. The record layer therefore
 * re-orders on the way OUT as well as on the way in, and this function is the one place
 * that order is written down.
 */
export function orderedFileItem(item: RecordFileItem): RecordFileItem {
  return {
    fileId: item.fileId,
    label: item.label,
    byteCount: item.byteCount,
    contentType: item.contentType,
  };
}

/** The cap for one FILES field: its setting, or the default when it has none. */
export function maxFilesOf(maxFiles: number | undefined): number {
  if (maxFiles === undefined) return FILES_FIELD_DEFAULT_MAX;
  return Math.min(Math.max(Math.trunc(maxFiles), 1), FILES_FIELD_MAX);
}

const MEBIBYTE = 1024 * 1024;
const GIBIBYTE = 1024 * MEBIBYTE;

/** One decimal below ten, whole numbers above: "19.4 GB", "20 GB", "7.2 MB", "480 MB". */
function scaled(value: number, unit: string): string {
  return `${value >= 10 ? Math.round(value) : Math.round(value * 10) / 10} ${unit}`;
}

/**
 * A size a person reads, not a size a machine reads. Whole MB above a megabyte, one
 * decimal between 100 kB and a megabyte, whole kB below - because "0.02 MB" says less
 * about a 20 kB file than "20 kB" does.
 *
 * THE GB BRANCH IS FOR THE BUDGET, not for a file. No attachment can reach a gibibyte -
 * `FILE_MAX_BYTES` is 25 MiB - but `ATTACHMENT_TOTAL_BYTES_MAX` is 20 GiB, and section 12's
 * refusal reads "using 19.4 GB of its 20 GB attachment storage". Without this branch that
 * sentence would say "20480 MB", which is a number nobody pictures.
 */
export function formatBytes(byteCount: number): string {
  if (byteCount >= GIBIBYTE) return scaled(byteCount / GIBIBYTE, 'GB');
  if (byteCount >= MEBIBYTE) return scaled(byteCount / MEBIBYTE, 'MB');
  if (byteCount >= 1024) return `${Math.max(1, Math.round(byteCount / 1024))} kB`;
  return `${byteCount} bytes`;
}

/**
 * THE OVER-THE-CAP SENTENCE, IN ONE PLACE (section 12).
 *
 * The browser pre-checks the size and the reserve route refuses on it, and both say the
 * same words with the same numbers. Two spellings of one limit is how a user ends up
 * reading "25 MB" on the screen and "26214400 bytes" in a toast.
 */
export function overSizeMessage(byteCount: number): string {
  const limit = formatBytes(FILE_MAX_BYTES);
  const size = formatBytes(byteCount);
  /**
   * A SENTENCE MUST NOT NAME TWO EQUAL NUMBERS (fresh-context review of wave 1, R4).
   *
   * `formatBytes` rounds to whole megabytes above 10 MB, so every size from the cap up to
   * 25.5 MB renders as "25 MB" - and the refusal read "That file is 25 MB and the limit is
   * 25 MB.", which tells a user their file is exactly the size that is allowed. Adding a
   * decimal does not fix it either: one byte over the cap is 25.0 MB.
   *
   * So the collision gets its OWN sentence, which is true across the whole window it covers
   * and still names the limit once. Everything below 25 MB keeps the two-number form, which
   * is the one that says what to do about it.
   */
  if (size === limit) return `That file is just over the ${limit} limit.`;
  return `That file is ${size} and the limit is ${limit}.`;
}

/** Section 12, and the reserve route refuses a zero-byte declaration with it. */
export const EMPTY_FILE_MESSAGE = 'That file is empty.';

/** Section 12: the count cap, stated with the field's own number. */
export function countCapMessage(maxFiles: number): string {
  return `This field already holds ${maxFiles} files, which is its limit. Remove one before adding another.`;
}

/**
 * D21's INSTANCE BUDGET REFUSAL, with BOTH numbers in it (section 12).
 *
 * The used figure is `max(ledgerSum, measuredBytes)` and the caller is the one that takes
 * that maximum: a ledger sum counts an orphan as zero, so an instance can sit at 19 GiB of
 * real disk while a ledger-only refusal reports 3 GB.
 *
 * A SENTENCE MUST NOT NAME TWO EQUAL NUMBERS, which is the rule `overSizeMessage` above
 * learned the hard way in wave 1b: `formatBytes` rounds to whole GB past ten, so everything
 * from 19.5 GiB up renders as "20 GB" and the refusal would read "using 20 GB of its 20 GB",
 * which tells an operator their instance is exactly the size that is allowed. The collision
 * gets its own sentence, true across the whole window it covers, naming the limit once.
 */
export function instanceBudgetMessage(usedBytes: number, capBytes: number): string {
  const used = formatBytes(usedBytes);
  const cap = formatBytes(capBytes);
  const tail = 'Delete some attachments, or ask the operator to raise the limit.';
  if (used === cap) return `This instance has used all of its ${cap} attachment storage. ${tail}`;
  return `This instance is using ${used} of its ${cap} attachment storage. ${tail}`;
}

/**
 * D21's FREE-SPACE REFUSAL (section 12). The one that stops this CRM filling a disk it
 * shares with three other live systems up to its own cap.
 *
 * No equal-numbers guard is needed here: the two numbers are a disk remainder and one
 * upload, orders of magnitude apart by the time the floor bites.
 */
export function diskSpaceMessage(freeBytes: number, neededBytes: number): string {
  return `The server has ${formatBytes(freeBytes)} of disk left and this upload needs ${formatBytes(neededBytes)}. Ask the operator to free space before uploading anything else.`;
}

/**
 * D12's MAPPING REFUSAL, AND IT IS THE POINT OF THE WHOLE DECISION (section 12).
 *
 * A Files field IS offered as a mapping target and answers with this when it is chosen.
 * Twenty hides the field and drops the column silently (research trap 7), so a person who
 * expected their attachments to arrive gets no error and no data. The house rule is that
 * every refusal gets a visible sentence, and a field the user cannot see cannot carry one.
 *
 * ONE STRING, TWO HALVES: the mapping screen refuses before it sends, and the mapping PATCH
 * refuses on the server, and both say exactly this - the same discipline `overSizeMessage`
 * follows for the size cap.
 */
export const FILES_NOT_IMPORTABLE_MESSAGE =
  'Files columns cannot be imported. A CSV can carry a filename but not the file. Upload attachments on the record after the import.';

/**
 * D12's EXPORT NOTE, on the export screen when the selection includes a Files field
 * (section 12). The column carries the LABELS, which is the only part of an attachment that
 * means anything to somebody reading a spreadsheet - an id means nothing outside this
 * instance, and a URL is either useless or a capability leak.
 */
export const FILES_EXPORT_NOTE =
  'Files columns export the file names only. The files themselves stay in the CRM.';

/**
 * D15's SENTENCE, and its two surfaces (section 12).
 *
 * The GUARD is the write path's refusal on `actor.source === 'AGENT'`, whatever the value
 * (D7). This is the MESSAGE: `proposeWrite` repeats the refusal early, keyed on the field
 * TYPE, so an agent reads why instead of meeting a typed error from three layers down; and
 * the approval screen says what will happen instead, because a proposal stored before that
 * layer existed still renders in a queue.
 */
export const AGENT_FILES_REFUSAL = 'Attachments cannot be changed by an agent.';

/**
 * Section 12, the approval screen. Since 2026-08-24 `approvePendingChange` refuses a
 * FILES-carrying agent proposal BEFORE the claim, so the proposal stays pending and the
 * honest instruction is "reject it" rather than "approving will do nothing".
 */
export const APPROVAL_FILES_REFUSAL = `${AGENT_FILES_REFUSAL} This proposal cannot be approved: reject it, and upload files on the record.`;

/**
 * D9's MERGE SENTENCE (section 12), on the review screen under the field list.
 *
 * The screen offers no Files ROW to choose between - `decideSurvivorship` excludes the type,
 * so a field with no decision has no row - and an absence explains nothing. The consequence
 * is real and it is the one an operator has to know before approving: the loser's contracts
 * stay on the loser, which is soft-deleted with them.
 */
export const MERGE_FILES_NOTE =
  'Attachments stay with the record they were uploaded to. Files on the record being merged away are not moved.';
